import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FaTrash, FaPlus, FaTimes } from 'react-icons/fa';

const DateSelectionPopup = ({
  isOpen,
  selectedDates,
  position,
  onClose,
  onRemoveEvents,
  onAddAllDayEvents,
  eventsCount = 0
}) => {
  const { theme } = useTheme();

  if (!isOpen || selectedDates.length === 0) return null;

  const popupBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-gray-200' : 'text-gray-800';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const borderColor = theme === 'dark' ? 'border-gray-600' : 'border-gray-300';

  const formatDateRange = () => {
    if (selectedDates.length === 1) {
      return selectedDates[0].toLocaleDateString('it-IT');
    }
    const sortedDates = [...selectedDates].sort((a, b) => a - b);
    return `${sortedDates[0].toLocaleDateString('it-IT')} - ${sortedDates[sortedDates.length - 1].toLocaleDateString('it-IT')}`;
  };

  // ✅ MIGLIORATO: Calcolo posizione più intelligente
  const getPopupStyle = () => {
    const popupWidth = 300;
    const popupHeight = 200;
    
    let left = position.x;
    let top = position.y;
    let transform = 'translateX(-50%)'; // Centro orizzontalmente per default
    
    // ✅ Calcola se il popup va fuori a destra
    if (position.x + popupWidth / 2 > window.innerWidth) {
      transform = 'translateX(-100%)'; // Allinea a destra dell'elemento
      left = position.x - 10;
    }
    
    // ✅ Calcola se il popup va fuori a sinistra
    if (position.x - popupWidth / 2 < 0) {
      transform = 'translateX(0)'; // Allinea a sinistra dell'elemento
      left = position.x + 10;
    }
    
    // ✅ Calcola se il popup va fuori in basso
    if (position.y + popupHeight > window.innerHeight) {
      top = position.y - popupHeight - 10; // Mostra sopra
    }
    
    return {
      top: Math.max(10, top), // Non andare mai sopra i 10px dal top
      left: Math.max(10, Math.min(window.innerWidth - popupWidth - 10, left)),
      transform,
      position: 'fixed',
      zIndex: 50
    };
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div
        className={`
          ${popupBg} rounded-lg shadow-xl border ${borderColor}
          p-4 min-w-[280px] max-w-[400px]
        `}
        style={getPopupStyle()} // ✅ Usa il posizionamento intelligente
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className={`font-semibold ${textColor}`}>
              {selectedDates.length} {selectedDates.length === 1 ? 'Giorno' : 'Giorni'} Selezionati
            </h3>
            <p className={`text-sm ${mutedText}`}>
              {formatDateRange()}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${mutedText}`}
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        {/* Eventi esistenti info */}
        {eventsCount > 0 && (
          <div className={`text-sm ${mutedText} mb-3 p-2 rounded bg-gray-50 dark:bg-gray-700/50`}>
            <span className="font-medium">{eventsCount}</span> eventi trovati nelle date selezionate
          </div>
        )}

        {/* Azioni */}
        <div className="space-y-2">
          {/* Rimuovi eventi */}
          <button
            onClick={() => {
              onRemoveEvents(selectedDates);
              onClose();
            }}
            disabled={eventsCount === 0}
            className={`
              w-full flex items-center gap-3 p-3 rounded-lg transition-colors
              ${eventsCount > 0 
                ? 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }
            `}
            title={eventsCount === 0 ? 'Nessun evento da rimuovere nelle date selezionate' : `Rimuovi ${eventsCount} eventi dalle date selezionate`}
          >
            <FaTrash className="h-4 w-4" />
            <div className="flex-1 text-left">
              <div className="font-medium">Rimuovi Eventi</div>
              <div className="text-xs opacity-75">
                {eventsCount === 0 
                  ? 'Nessun evento presente'
                  : `Elimina ${eventsCount} eventi dalle date selezionate`
                }
              </div>
            </div>
          </button>

          {/* Aggiungi eventi giornalieri */}
          <button
            onClick={() => {
              onAddAllDayEvents(selectedDates);
              onClose();
            }}
            className={`
              w-full flex items-center gap-3 p-3 rounded-lg transition-colors
              bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30
              text-emerald-700 dark:text-emerald-300
            `}
          >
            <FaPlus className="h-4 w-4" />
            <div className="flex-1 text-left">
              <div className="font-medium">Aggiungi Eventi Giornalieri</div>
              <div className="text-xs opacity-75">
                Crea eventi per giornate intere nelle date selezionate
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className={`text-xs ${mutedText} mt-3 pt-2 border-t ${borderColor}`}>
          Tip: Click altrove per chiudere questo popup
        </div>
      </div>
    </>
  );
};

export default DateSelectionPopup;